'use client'

import { motion, useReducedMotion } from 'motion/react'
import { cn } from '../../../../lib/cn'

export interface GridSparseCenterProps {
  /**
   * Enable entrance animation
   * @default true
   */
  animate?: boolean

  /**
   * Animation delay in seconds (allows hero content to load first)
   * @default 0.5
   */
  animationDelay?: number

  className?: string
}

/**
 * GridSparseCenter - Large-cell grid pattern with center fade
 *
 * Continuous 1px lines with 40px cells that fade from the center.
 * Open, airy feel ideal for minimalist centered layouts.
 *
 * Fixed fadeSize: 0.50
 *
 * @example
 * ```tsx
 * <section className="relative">
 *   <GridSparseCenter />
 *   <div className="relative z-10">Content here</div>
 * </section>
 * ```
 */
export function GridSparseCenter({
  animate = true,
  animationDelay = 0.5,
  className,
}: GridSparseCenterProps = {}) {
  const prefersReducedMotion = useReducedMotion()
  const fadeSize = 0.5
  const size = `${fadeSize * 100}%`
  const maskGradient = `radial-gradient(ellipse ${size} ${size} at 50% 50%, #000 40%, transparent 100%)`

  return (
    <motion.div
      className={cn('absolute inset-0 z-0', className)}
      style={{
        backgroundImage: `
          linear-gradient(to right, color-mix(in srgb, var(--border), transparent 50%) 1px, transparent 1px),
          linear-gradient(to bottom, color-mix(in srgb, var(--border), transparent 50%) 1px, transparent 1px)
        `,
        backgroundSize: '40px 40px',
        maskImage: maskGradient,
        WebkitMaskImage: maskGradient,
      }}
      initial={animate ? { opacity: 0 } : false}
      animate={animate ? { opacity: 1 } : false}
      transition={{
        duration: prefersReducedMotion ? 0.3 : 1.2,
        delay: animate ? (prefersReducedMotion ? 0 : animationDelay) : 0,
        ease: [0.16, 1, 0.3, 1],
      }}
    />
  )
}
